import { useState } from "react";
import axios from "axios";
import { Mail, Linkedin, CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, CONTACT_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  company: "",
  website: "",
  company_size: "",
  testing_requirement: "",
  qa_setup: "",
  message: "",
};

const inputCls =
  "h-12 rounded-xl border-zinc-800 bg-ink-900/70 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-volt/60 focus-visible:border-volt/50";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid work email.";
    if (form.message.trim().length < 10) e.message = "Tell us a little more (min. 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      // One automatic retry shields users from transient network/deploy blips
      try {
        await new Promise((r) => setTimeout(r, 1500));
        await axios.post(`${API}/contact`, form);
        setStatus("success");
      } catch (retryErr) {
        setStatus("error");
      }
    }
  };

  return (
    <>
      <Seo
        title="Contact Avirova Technologies — Get a Free QA Consultation"
        description="Tell us what you're building, where you're facing testing challenges, and when your next release is scheduled. Request a free QA consultation."
      />

      <section className="relative overflow-hidden pt-36 pb-16 lg:pt-44" data-testid="contact-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              {"// Contact"}
            </p>
          </MaskedLine>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-black leading-[0.98] tracking-tighter text-zinc-100 sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.2}>
              Let's talk about your <span className="text-volt">product.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.36}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              Tell us what you're building, where you're facing testing
              challenges, and when your next release is scheduled.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32" data-testid="contact-section">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <Reveal>
              {status === "success" ? (
                <div
                  data-testid="contact-success-message"
                  className="flex h-full min-h-[24rem] flex-col items-start justify-center rounded-2xl border border-volt/30 bg-volt/[0.04] p-10"
                >
                  <CheckCircle2 className="h-10 w-10 text-volt" />
                  <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-zinc-100">
                    Enquiry received.
                  </h2>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">
                    Thanks for reaching out. We've logged your enquiry and will
                    get back to you shortly to schedule your free QA consultation.
                  </p>
                  <button
                    data-testid="contact-send-another-button"
                    onClick={() => { setForm(initialForm); setStatus("idle"); }}
                    className="mt-8 rounded-full border border-zinc-700 px-6 py-3 text-sm font-semibold text-zinc-200 transition-colors duration-300 hover:border-volt/50 hover:text-volt"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  data-testid="contact-form"
                  className="rounded-2xl border border-zinc-800 bg-ink-900/60 p-7 sm:p-10"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="cf-name" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Name *</Label>
                      <Input id="cf-name" data-testid="contact-name-input" className={`mt-2.5 ${inputCls}`} placeholder="Jane Cooper" value={form.name} onChange={(e) => set("name", e.target.value)} />
                      {errors.name && <p data-testid="contact-name-error" className="mt-2 text-xs text-red-400">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="cf-email" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Work Email *</Label>
                      <Input id="cf-email" type="email" data-testid="contact-email-input" className={`mt-2.5 ${inputCls}`} placeholder="jane@company.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                      {errors.email && <p data-testid="contact-email-error" className="mt-2 text-xs text-red-400">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="cf-company" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Company</Label>
                      <Input id="cf-company" data-testid="contact-company-input" className={`mt-2.5 ${inputCls}`} placeholder="Acme Inc." value={form.company} onChange={(e) => set("company", e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="cf-website" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Website / Product URL</Label>
                      <Input id="cf-website" data-testid="contact-website-input" className={`mt-2.5 ${inputCls}`} placeholder="https://yourproduct.com" value={form.website} onChange={(e) => set("website", e.target.value)} />
                    </div>
                    <div>
                      <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Company Size</Label>
                      <Select value={form.company_size} onValueChange={(v) => set("company_size", v)}>
                        <SelectTrigger data-testid="contact-company-size-select" className={`mt-2.5 ${inputCls}`}>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-800 bg-ink-900 text-zinc-200">
                          {CONTACT_OPTIONS.companySize.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`company-size-option-${o}`}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">What do you need help with?</Label>
                      <Select value={form.testing_requirement} onValueChange={(v) => set("testing_requirement", v)}>
                        <SelectTrigger data-testid="contact-requirement-select" className={`mt-2.5 ${inputCls}`}>
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-800 bg-ink-900 text-zinc-200">
                          {CONTACT_OPTIONS.requirement.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`requirement-option-${o.toLowerCase().replace(/[\s/]+/g, "-")}`}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Current QA Setup</Label>
                      <Select value={form.qa_setup} onValueChange={(v) => set("qa_setup", v)}>
                        <SelectTrigger data-testid="contact-qa-setup-select" className={`mt-2.5 ${inputCls}`}>
                          <SelectValue placeholder="Select your current setup" />
                        </SelectTrigger>
                        <SelectContent className="border-zinc-800 bg-ink-900 text-zinc-200">
                          {CONTACT_OPTIONS.qaSetup.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`qa-setup-option-${o.toLowerCase().replace(/[\s/()]+/g, "-")}`}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="cf-message" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Message *</Label>
                      <Textarea
                        id="cf-message"
                        data-testid="contact-message-input"
                        rows={5}
                        className="mt-2.5 rounded-xl border-zinc-800 bg-ink-900/70 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-volt/60 focus-visible:border-volt/50"
                        placeholder="Tell us about your product, release cycle and where testing is hurting most…"
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                      />
                      {errors.message && <p data-testid="contact-message-error" className="mt-2 text-xs text-red-400">{errors.message}</p>}
                    </div>
                  </div>

                  {status === "error" && (
                    <p data-testid="contact-error-message" className="mt-6 rounded-xl border border-red-400/30 bg-red-400/[0.06] px-4 py-3 text-sm text-red-300">
                      Something went wrong while sending your enquiry. Please try again, or email us directly at {COMPANY.email}.
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "sending"}
                    className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-volt px-8 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 sm:w-auto"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Request a Free QA Consultation
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-zinc-800 bg-ink-900/60 p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">
                  {"// Direct"}
                </p>
                <div className="mt-6 space-y-5">
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="group flex items-center gap-3 text-sm text-zinc-300 transition-colors duration-300 hover:text-volt"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-800 bg-ink-950">
                      <Mail className="h-4 w-4" />
                    </span>
                    {COMPANY.email}
                  </a>
                  <div className="flex items-center gap-3 text-sm text-zinc-300" data-testid="contact-linkedin-link">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-800 bg-ink-950">
                      <Linkedin className="h-4 w-4" />
                    </span>
                    <span className="text-zinc-500">{COMPANY.linkedin}</span>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-4 rounded-2xl border border-zinc-800 bg-ink-900/60 p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">
                  {"// What happens next"}
                </p>
                <ol className="mt-6 space-y-5">
                  {[
                    "We review your product, release cycle and testing challenges.",
                    "We schedule a free consultation call to understand scope.",
                    "You receive a proposed QA setup with a clear engagement model.",
                  ].map((step, i) => (
                    <li key={step} className="flex gap-4">
                      <span className="font-mono text-xs text-volt">{String(i + 1).padStart(2, "0")} //</span>
                      <span className="text-sm leading-relaxed text-zinc-400">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
